import { type JSX } from "react";
import { Box, Flex, Stack, type StyleProps, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Icon } from "../icon/Icon";
import {
  RouterLink,
  type RouterLinkProps,
} from "components/routerLink/RouterLink";
import { useProjectId } from "api/hooks/useProjectId";

interface BackToDashboardButtonProps extends StyleProps {
  to?: RouterLinkProps["to"];
  label?: string;
}

export const BackToDashboardButton = ({
  to,
  label = "Back to dashboard",
  ...customStyles
}: BackToDashboardButtonProps) => {
  const projectId = useProjectId();
  const resolvedTo =
    to ?? (`/projects/${projectId}/services` as RouterLinkProps["to"]);

  return (
    <Flex
      as={RouterLink}
      to={resolvedTo}
      gap="2"
      alignItems="center"
      color="fg.emphasized"
      cursor="pointer"
      {...customStyles}
    >
      <Icon name="actions/Arrow/Simple/Left" />
      {label}
    </Flex>
  );
};

export interface PageFrameProps extends StyleProps {
  title?: JSX.Element | string;
  intro?: JSX.Element | string;
  introCustomStyles?: StyleProps;
  tags?: ReactNode;
  image?: ReactNode;
  hasBackButton?: boolean;
  toolbar?: ReactNode | JSX.Element;
  children?: ReactNode;
}

/**
 * Provides the standard page layout for Tiger Cloud by organizing the page
 * header, optional toolbar, navigation, and primary content into a consistent
 * structure.
 */
export const PageFrame = ({
  title = "",
  tags,
  image,
  toolbar,
  intro = "",
  hasBackButton,
  children,
  introCustomStyles,
  ...customStyles
}: PageFrameProps) => {
  return (
    <Flex
      flex="1 1 auto"
      direction="column"
      px={{ base: "2", lg: "8" }}
      {...customStyles}
    >
      {hasBackButton && <BackToDashboardButton mb="6" />}
      {(toolbar || title || tags || image) && (
        <Flex direction="column" gap="2" mb="6">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            data-cy="page-frame_title"
            position="relative"
          >
            <Stack direction="row" gap="2" alignItems="center">
              {image}

              {tags}

              {title && typeof title === "string" ? (
                <Text textStyle="subtitle2" flex="1">
                  {title}
                </Text>
              ) : (
                title
              )}
            </Stack>

            {toolbar && (
              <Flex alignSelf="flex-start" ml="10">
                {toolbar}
              </Flex>
            )}
          </Flex>
          {intro &&
            (typeof intro === "string" ? (
              <Box
                as="span"
                color="fg.emphasized"
                fontSize="future.md"
                {...introCustomStyles}
              >
                {intro}
              </Box>
            ) : (
              intro
            ))}
        </Flex>
      )}

      {children}
    </Flex>
  );
};
